import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, failTask, fetchFailureReasonCodes, fetchTask } from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { useGeolocation } from '../geo/useGeolocation';
import { detailRequiredFor, selectableReasonCodes, validateFailForm } from '../lib/delivery-form';
import { formatAccuracy } from '../lib/format';
import { createIdempotencyKeyHolder } from '../lib/idempotency';
import { isTransitionLegal, type FieldTaskStatus } from '../lib/task-actions';
import { ErrorCard, LoadingState } from '../states';

/**
 * Form kegagalan pengiriman.
 *
 * **Alasan gagal wajib berupa KODE, bukan kalimat.** Tidak ada isian teks bebas yang bisa
 * menggantikan pilihan kode — itu inti ADR-0035: teks bebas tidak bisa dihitung, tidak bisa
 * dibandingkan antar-kurir, dan karenanya tidak pernah menjadi dasar perbaikan operasional.
 * Kolom keterangan ADA, tetapi sebagai pelengkap kode, bukan pengganti.
 */
export function TaskFailPage(): ReactNode {
  const session = useSession();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const geo = useGeolocation();
  const keys = useMemo(() => createIdempotencyKeyHolder(), []);

  const [reasonCodeId, setReasonCodeId] = useState('');
  const [detail, setDetail] = useState('');
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const { state } = useAsync(async () => {
    const [task, codes] = await Promise.all([
      fetchTask(session.auth, id),
      fetchFailureReasonCodes(session.auth),
    ]);
    return { task, codes };
  }, [session.auth, id]);

  if (state.status === 'loading') return <LoadingState label="Memuat alasan kegagalan…" />;
  if (state.status === 'error') return <ErrorCard error={state.error} />;

  const { task, codes } = state.data;
  const selectable = selectableReasonCodes(codes);
  const problems = validateFailForm({ reasonCodeId, detail }, codes);
  const problemFor = (field: string) =>
    submitted ? (problems.find((p) => p.field === field)?.message ?? null) : null;

  // `fail` legal dari `started` dan `arrived` saja.
  const canFail =
    isTransitionLegal(task.status as FieldTaskStatus, 'cancel') &&
    (task.status === 'started' || task.status === 'arrived');

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitted(true);
    setError(null);
    if (problems.length > 0) return;

    const selected = codes.find((c) => c.id === reasonCodeId);
    if (!selected) return;

    setSending(true);
    try {
      await failTask(session.auth, task.id, keys.current(), {
        failureReasonCode: selected.code,
        failureDetail: detail.trim() || null,
        // Kosong sampai Issue #466 — unggah bukti belum punya layarnya.
        photoFileIds: [],
        latitude: geo.location.latitude,
        longitude: geo.location.longitude,
        accuracyMeters: geo.location.accuracyMeters,
        note: note.trim() || null,
      });
      keys.rotate();
      navigate(`/tasks/${task.id}`);
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <section className="card">
        <Link to={`/tasks/${task.id}`}>← Detail tugas</Link>
        <h2>Laporkan kegagalan</h2>
        <p className="muted small">
          {task.taskNumber} · {task.title}
        </p>
      </section>

      {!canFail ? (
        <section className="card" role="status">
          <p className="warning">Kegagalan hanya dapat dilaporkan setelah tugas dimulai.</p>
          <p className="muted small">
            Mulai tugas lebih dulu dari halaman detail. Bila tugas memang tidak jadi dikerjakan,
            gunakan Batalkan.
          </p>
        </section>
      ) : (
        <form className="card" onSubmit={submit} noValidate>
          <label htmlFor="reason">Alasan kegagalan</label>
          <select
            id="reason"
            value={reasonCodeId}
            onChange={(e) => {
              setReasonCodeId(e.target.value);
              setDetail('');
            }}
          >
            <option value="">Pilih alasan…</option>
            {selectable.map((code) => (
              <option key={code.id} value={code.id}>
                {code.label}
              </option>
            ))}
          </select>
          <p className="muted small">
            Alasan dipilih dari daftar supaya kegagalan dapat dihitung dan diperbaiki. Tidak ada
            isian bebas yang menggantikannya.
          </p>
          {problemFor('reasonCodeId') ? (
            <p className="warning small" role="alert">
              {problemFor('reasonCodeId')}
            </p>
          ) : null}

          {detailRequiredFor(reasonCodeId, codes) ? (
            <>
              <label htmlFor="detail">Keterangan (wajib untuk alasan ini)</label>
              <textarea
                id="detail"
                rows={3}
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
              />
              {problemFor('detail') ? (
                <p className="warning small" role="alert">
                  {problemFor('detail')}
                </p>
              ) : null}
            </>
          ) : null}

          <label htmlFor="note">Catatan tambahan (opsional)</label>
          <input id="note" value={note} onChange={(e) => setNote(e.target.value)} />

          {/* Akurasi sebagai ANGKA sebelum kirim — bukan ikon yang menenangkan. */}
          <p className={geo.status === 'ready' ? 'small' : 'warning small'}>
            {formatAccuracy(geo.location.accuracyMeters)}
            {geo.status !== 'ready' ? ' — lokasi belum siap' : ''}
          </p>

          <button type="submit" className="button-primary" disabled={sending}>
            {sending ? 'Mengirim…' : 'Laporkan gagal'}
          </button>
        </form>
      )}

      {error ? <ErrorCard error={error} /> : null}
    </>
  );
}
